import { useHelper } from "@hooks/useHelper";
import { useImage } from "@hooks/useImage";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type GatsbyCustomImage,
  type ResolvedLink,
} from "@ts/components";
import {
  type ContentSection,
  type SanityKeyed,
  type SectionImageCarousel,
} from "@ts/sanity";
import React, { type FC } from "react";

export type ImageCarouselInputProps = ComponentProps &
  FromSanityGraphQL<SectionImageCarousel> & {
    imageCarouselContent?: Array<SanityKeyed<ContentSection>>;
  };

export type ImageCarouselOutputProps = ImageCarouselInputProps & {
  images?: GatsbyCustomImage[] | null;
  onClickImage: (link?: ResolvedLink) => void;
};

export const withImageCarousel =
  (Component: FC<ImageCarouselOutputProps>) =>
  ({
    name = "ImageCarousel",
    analytics,
    content,
    imageCarouselContent,
    ...rest
  }: ImageCarouselInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const { handleClickOnCustomContentLink } = useHelper();
    const normalisedContents = content ?? imageCarouselContent;
    const images = normalisedContents?.map((normalisedContent) => {
      const image = normalisedContent?.image;
      const imageLink =
        image?.imagelink && image?.imagelink?.length
          ? linkResolver(image?.imagelink[0])
          : null;

      return {
        ...getFluidImage(image, { layout: "fullWidth" }),
        imageLink: imageLink,
      };
    });

    Component.displayName = name;

    if (!images?.length) {
      return null;
    }

    return (
      <Component
        {...rest}
        images={images}
        onClickImage={handleClickOnCustomContentLink}
      />
    );
  };
